import React from 'react'
import { css } from '@emotion/core'
import { string } from 'prop-types'
import { defaultFills } from '../../pages/KidsHome/BackgroundFills'
import { BackgroundFillsType } from '../../sharedPropTypes/activityProps'

// Generate deterministic-ish animation config for each cloud on module load.
// We keep the pairing of delay + duration together to avoid unnatural motion
// patterns and layout jitter on weaker devices.
const randAnime = (base = 120, multi = 10) => {
  const safeBase = typeof base === 'number' && !Number.isNaN(base) ? base : 120
  const safeMulti = typeof multi === 'number' && !Number.isNaN(multi) ? multi : 10
  const factor = Math.max(1, Math.ceil(safeMulti * Math.random()))
  return {
    duration: 1.5 * safeBase + factor,
    delay: factor,
  }
}

// Precompute once so values remain stable for the session (avoids re-calculation during re-renders)
const cloudConfigs = [
  randAnime(), // cloud-1
  randAnime(100), // cloud-2
  randAnime(), // cloud-3
  randAnime(150, 6), // cloud-4
  randAnime(), // cloud-5
  randAnime(200), // cloud-6
]

// Helper to safely access config (fallback keeps animation working even if array changes)
const cfg = (i) => cloudConfigs[i] || { duration: 180, delay: 0 }

const cloudAnimationCss = cloudConfigs
  .map(
    (_config, index) => `
  .cloud-${index + 1} {
    animation-delay: ${cfg(index).delay}s;
    animation-duration: ${cfg(index).duration}s;
  }
`
  )
  .join('')

const landingBgCss = css`
  @keyframes drift {
    from {
      transform: translateX(-1300px);
    }
    to {
      transform: translateX(1400px);
    }
  }
  position: absolute;
  width: 100%;
  height: 100%;
  g,
  svg {
    transition: opacity 0.3s ease-in-out;
  }
  path {
    transition: fill 0.3s ease-in-out;
  }
  .cloud {
    transform: translateX(-1300px);
    animation-name: drift;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
  }
  ${cloudAnimationCss}
`

const cloudPaths = [
  {
    id: 1,
    fillKey: 'clouds1',
    d: 'M1216.96 227.274C1217.73 223.765 1220.05 220.714 1223.37 218.866C1226.69 217.017 1230.7 216.54 1234.42 217.55C1237.52 209.726 1246.58 205.389 1255.29 207.556C1264 209.724 1269.48 217.678 1267.91 225.886C1270.53 223.071 1281 223.071 1281 232H1206C1206 228.429 1211.85 224.988 1216.96 227.274Z',
  },
  {
    id: 2,
    fillKey: 'clouds1',
    d: 'M178.155 199.121C181.633 199.123 184.849 200.864 186.614 203.697C188.087 201.306 191.259 200.391 193.893 201.598C194.434 199.071 196.072 196.874 198.407 195.543C200.742 194.212 203.561 193.869 206.177 194.596C208.364 188.963 214.738 185.84 220.867 187.401C226.995 188.961 230.849 194.688 229.747 200.598C230.347 200.446 230.966 200.367 231.588 200.365C233.967 200.361 236.2 201.449 237.579 203.282C238.026 203.172 238.487 203.116 238.949 203.115C239.319 203.114 239.688 203.149 240.05 203.219C241.328 201.571 243.494 200.763 245.619 201.143C247.743 201.524 249.447 203.024 250 205H169C170.488 201.451 174.117 199.121 178.155 199.121Z',
  },
  {
    id: 3,
    fillKey: 'clouds2',
    d: 'M564.234 168.219C563.619 165.412 561.757 162.971 559.102 161.492C556.447 160.013 553.242 159.632 550.267 160.44C547.781 154.181 540.534 150.711 533.566 152.445C526.598 154.179 522.216 160.542 523.469 167.109C521.372 164.857 513 164.857 513 172H573C573 169.143 568.32 166.39 564.234 168.219Z',
  },
  {
    id: 4,
    fillKey: 'clouds2',
    d: 'M862.155 146.121C865.633 146.123 868.849 147.864 870.614 150.697C872.087 148.306 875.259 147.391 877.893 148.598C878.434 146.071 880.072 143.874 882.407 142.543C884.742 141.212 887.561 140.869 890.177 141.596C892.364 135.963 898.738 132.84 904.867 134.401C910.995 135.961 914.849 141.688 913.747 147.598C914.347 147.446 914.966 147.367 915.588 147.365C917.967 147.361 920.2 148.449 921.579 150.282C922.026 150.172 922.487 150.116 922.949 150.115C923.319 150.114 923.688 150.149 924.05 150.219C925.328 148.571 927.494 147.763 929.619 148.143C931.743 148.524 933.447 150.024 934 152H853C854.488 148.451 858.117 146.121 862.155 146.121Z',
  },
  {
    id: 5,
    fillKey: 'clouds2',
    d: 'M564.234 168.219C563.619 165.412 561.757 162.971 559.102 161.492C556.447 160.013 553.242 159.632 550.267 160.44C547.781 154.181 540.534 150.711 533.566 152.445C526.598 154.179 522.216 160.542 523.469 167.109C521.372 164.857 513 164.857 513 172H573C573 169.143 568.32 166.39 564.234 168.219Z',
  },
  {
    id: 6,
    fillKey: 'clouds1',
    d: 'M178.155 199.121C181.633 199.123 184.849 200.864 186.614 203.697C188.087 201.306 191.259 200.391 193.893 201.598C194.434 199.071 196.072 196.874 198.407 195.543C200.742 194.212 203.561 193.869 206.177 194.596C208.364 188.963 214.738 185.84 220.867 187.401C226.995 188.961 230.849 194.688 229.747 200.598C230.347 200.446 230.966 200.367 231.588 200.365C233.967 200.361 236.2 201.449 237.579 203.282C238.026 203.172 238.487 203.116 238.949 203.115C239.319 203.114 239.688 203.149 240.05 203.219C241.328 201.571 243.494 200.763 245.619 201.143C247.743 201.524 249.447 203.024 250 205H169C170.488 201.451 174.117 199.121 178.155 199.121Z',
  },
]

export default function SimpleLandingPageBackground({ fillColors = defaultFills, height = '100%' }) {
  return (
    <div
      aria-hidden="true"
      css={landingBgCss}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height,
      }}
    >
      <svg
        viewBox="0 0 1440 422"
        fill="none"
        role="img"
        preserveAspectRatio="xMidYMid slice"
        style={{ width: '100%', maxWidth: '1440px', minWidth: '1240px', height: height ?? 'auto' }}
      >
        <g clipPath="url(#clip0)">
          <rect width="1440" height="422" fill="url(#paint0_linear)" />
          {cloudPaths.map(({ id, d, fillKey }) => (
            <path
              key={id}
              className={`cloud cloud-${id}`}
              d={d}
              fill={fillColors[fillKey]}
              data-testid={`cloud-${id}`}
            />
          ))}

          <path
            d="M909.724 331.761C891.803 343.531 869.389 341.078 869.389 341.078L934.293 298.457C934.306 298.452 927.645 319.991 909.724 331.761Z"
            fill={fillColors.base}
          />
        </g>
        <defs>
          <linearGradient id="paint0_linear" x1="720" y1="0" x2="719.851" y2="158.118" gradientUnits="userSpaceOnUse">
            <stop stopColor={fillColors.gradient1} />
            <stop offset="1" stopColor={fillColors.gradient2} />
          </linearGradient>
        </defs>
      </svg>
    </div>
  )
}

SimpleLandingPageBackground.propTypes = {
  height: string,
  fillColors: BackgroundFillsType,
}
